import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { Table, useTable, Layout, MainLoader, TableCounter, Button, InlineNotification, ExternalLink, Text, Tooltip } from "@netapp/bxp-design-system-react";
import _ from "lodash";
import styles from "./ValidateAgent.module.scss";
import { useDispatch, useSelector } from "react-redux";
import tableStyles from '@netapp/bxp-design-system-react/styles/Tables/Table.module.scss';
import { useTranslation } from 'react-i18next';
import { connectorsettings as i18nNamespace } from '../../../../i18n/namespace';
import { DownloadMonochromeIcon } from '@netapp/bxp-design-system-react/icons/monochrome';
import { getAgentValidationStatuses } from 'modules/connectors/utils';
import { LEARN_MORE_LINK_AGENT_VALIDATION } from 'modules/add-agent/pdf-utils/pdfConstants';
import { handleDownloadReport } from './handleDownloadReport';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const StatusRenderer = (getAgentValidationStatuses, t) => ({ value, row }) => {
    
    let statusCheckFailedMessage = '';
    const getStatusIcon = () => {
        statusCheckFailedMessage = '';
        const validationStatus = getAgentValidationStatuses[value];
        const label = validationStatus?.displayName;
        statusCheckFailedMessage = row?.status === "FAILED" ? row.message : ""

        switch (value) {
            case "IN_PROGRESS":
                return (
                    <InlineNotification type="info">
                        {label}
                    </InlineNotification>
                );
            case "FAILED":
                return (
                    <InlineNotification type="error">
                        {label}
                    </InlineNotification>
                );
            case "SUCCESS":
                return (
                    <InlineNotification type="success">
                        {label}
                    </InlineNotification>
                );
            default:
                return <span>{label}</span>;
        }
    };

    return (
        <div className={styles['status-cell']}>
            {getStatusIcon()}
            {statusCheckFailedMessage && <Tooltip>{statusCheckFailedMessage}</Tooltip>}
        </div>
    );
};

const descriptionRenderer = (t) => ({ value }) => {
    const descriptionMap = {
        NETWORK: 'verify_access',
        HARDWARE: 'verify_disk_space',
        PORTS: 'verify_ports'
    };

    const key = descriptionMap[value];
    
    return (
        <div>
            {key ? t(key) : ""}
        </div>
    );
};

const innerTableHeaderText = (t, type) => {
    const headerTextMap = {
        NETWORK: 'url',
        HARDWARE: 'mount_path',
        PORTS: 'ports'
    };

    const key = headerTextMap[type];
    return key ? t(key) : "";
};

export const SubRowRenderer = React.memo(({ row }) => {
    const { t } = useTranslation(i18nNamespace);
    const connectorsStatuses = getAgentValidationStatuses(t);
    const { tableColumns } = useMemo(() => {
        const tableColumns = [
            {
                header: innerTableHeaderText(t, row?.type),
                accessor: "data[0].value",
                id: "url"
            }, {
                header: t("status"),
                accessor: "status",
                id: "status",
                Renderer: StatusRenderer(connectorsStatuses, t)
            }];
        return { tableColumns }
    }, []);

    const dsRow = useTable({ columns: tableColumns, rows: row?.checks });

    return <div className={styles['subscription-wrapper']}>
        {<>
            {row?.checks?.length > 0 && <div className={styles['package-wrapper']}>
                {/* <SubTable columns={tableColumns} rows={dsRow?.rows} /> */}
                <Table
                    columns={tableColumns}
                    rows={dsRow?.rows}
                    innerTable />
            </div>}
        </>}
    </div>
});

export default ({ agentId, agentName}) => {

    const dispatch = useDispatch();
    const agentValidationData = useSelector(state => state.connectorSettings?.validateAgent);
    const isLoading = agentValidationData?.loader;
    const apiData = agentValidationData?.config;
    const validationCompleted = agentValidationData?.validationCompleted;
    const tableData = apiData ? apiData.services[0]?.validations : [];
    const { t } = useTranslation(i18nNamespace);
    const connectorsStatuses = getAgentValidationStatuses(t);

    const { tableColumns } = useMemo(() => {
        const tableColumns = [
            {
                header: t("verifications"),
                accessor: "type",
                id: "description",
                Renderer: descriptionRenderer(t)
            }, {
                header: t("status"),
                accessor: "validationStatus",
                id: "validationStatus",
                Renderer: StatusRenderer(connectorsStatuses, t)
            }];
        return { tableColumns }
    }, [dispatch, t])

    const { organizedRows, columns, rowState, updateRowState, selectionState, filterState, columnState, restoreColumnStateDefaults, updateColumnState } = useTable({
        columns: tableColumns,
        rows: tableData
    });

    const onRowClick = useCallback((row) => {
        updateRowState(row.id)(rowState => ({ isExpanded: !(rowState && rowState.isExpanded) }))
    }, [updateRowState]);

    // Handler for the "Download Report" button
    const downloadReport = () => {
        let jsonData;
        if (apiData && apiData.services?.length > 0) {
            jsonData = apiData;
        }
        handleDownloadReport({ json: jsonData, t, agentId, agentName });
    }
    
    // Expand first row by default when rows are ready
    const didExpandInitial = useRef(false);
    useEffect(() => {
        if (!didExpandInitial.current && organizedRows?.length > 0) {
            const firstRowId = organizedRows[0].id;
            updateRowState(firstRowId)(prev => ({ ...prev, isExpanded: true }));
            didExpandInitial.current = true;
        }
    }, [organizedRows, updateRowState]);
    
    if (isLoading) {
        return <MainLoader />
    } else {
        return <Layout.Grid centerContent>
            <Layout.GridItem lg={5}>
                {<TableCounter filteredCount={organizedRows?.length} totalCount={organizedRows?.length} selectedCount={selectionState?.count} pluralLabel={t("validation_agent_capital_plural")} singularLabel={t("validation_agent_capital")} />}
            </Layout.GridItem>
            <Layout.GridItem lg={7}>
                {<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Button title={validationCompleted ? "": t("download_status")} isDisabled={!validationCompleted} variant="icon" color="primary" style={{ width: '20px' }} onClick={downloadReport}><DownloadMonochromeIcon /></Button>
                    <Text className={styles["margin-left-learn-more"]}>
                        <ExternalLink
                            includeArrow={true}
                            href={LEARN_MORE_LINK_AGENT_VALIDATION}
                            variant="text"
                            newTab
                        >
                            {t("learn_more")}
                        </ExternalLink>
                    </Text>
                </div>}
            </Layout.GridItem>
            <Layout.GridItem lg={12} className={styles['table-margin']}>
                <Table
                    columns={columns}
                    isSticky
                    originalColumns={tableColumns}
                    columnState={columnState}
                    restoreColumnStateDefaults={restoreColumnStateDefaults}
                    updateColumnState={updateColumnState}
                    rows={organizedRows}
                    selectionState={selectionState}
                    onRowClick={onRowClick}
                    rowState={rowState}
                    cellClassName={tableStyles['large-cell']}
                    updateRowState={updateRowState}
                    stickColumnIndex={1}
                    SubRowRenderer={SubRowRenderer}
                />
            </Layout.GridItem>
        </Layout.Grid>
    }
}
